import React, {
  Reducer,
  useContext,
  useEffect,
  useReducer,
  useState
} from "react";

import MaterialTable from "@material-table/core";
import {
  Avatar,
  Badge,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Snackbar,
  List,
  ListItem,
  TextField,
  Tooltip,
  Typography,
  Card,
  CardHeader,
  CardContent,
  ListItemSecondaryAction,
  Divider
} from "@material-ui/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import useStyles from "./styles";
import {
  Features,
  FeatureType,
  Tenant,
  TenantConfType,
  TenantStatus,
  TenantType
} from "../../models/Tenant";
import { AppState } from "../../store";
import { PersonMultiSelect } from "../../components/selects/PersonMultiSelect";

import * as TenantApi from "../../api/tenantApi";
import { Alert, AlertTitle } from "@material-ui/lab";
import { ImageEditor } from "../../components/editors/ImageEditor";
import { ImageInformation } from "../../models/ImageInformation";
import { TenantTypeSelect } from "../../components/selects/TenantTypeSelect";
import { ReceiptEmailTemplateEditor } from "./EmailTemplates/ReceiptEmailTemplateEditor";
import {
  thunkFetchCurrentTenantLanguages,
  thunkFetchTenants
} from "../../store/tenants/actions";
import { MarketSelect } from "../../components/selects/MarketSelect";
import { Market } from "../../models/Market";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchMarkets } from "../../store/markets/actions";
import { getFlags } from "../../utils";
import { LanguageMultiSelect } from "../../components/selects/LanguageMultiSelect";
import { ManageTenantDistributorManagers } from "./components/ManageTenantDistributorManagers";
import {
  AttachEmail,
  HourglassTop,
  TaskAlt,
  Verified
} from "@mui/icons-material";

// @ts-ignore
import JSONInput from "react-json-editor-ajrm";
// @ts-ignore
import locale from "react-json-editor-ajrm/locale/en";
import { UserContext } from "../../UserProvider";
import { hasRole } from "../../models/User";
import { NotificationsActive } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";
import { useNavigate, useParams } from "react-router-dom";
import { Person } from "../../models/Person";
import { SupportEditor } from "./components/SupportEditor";
import { OnBoardingEditor } from "./components/OnBoardingEditor";
import { withPrimaryIcon } from "../stores/StoreList/components/styles";
import SettingsIcon from "@material-ui/icons/Settings";
import ListItemText from "@material-ui/core/ListItemText";
import produce from "immer";
import { VerificationMethodMultiSelect } from "../../components/selects/VerificationMethodMultiSelect";
import { PaymentProviderMultiSelect } from "../../components/selects/PaymentProviderMultiSelect";
import { ManageTenantDistributors } from "./components/ManageTenantDistributors";
import { LoginMethodOrderedSelects } from "../../components/selects/LoginMethodOrderedSelect";
import { useI18n } from "../../hooks/useI18n";

export const TenantList = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const user = useContext(UserContext);
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useI18n();

  const tenants =
    useSelector<AppState, ReadonlyArray<Tenant>>(s => s.tenants.list) || [];
  const markets = asList<Market>(
    useSelector<AppState, unknown>(s => s.markets)
  );

  useLoadIfEmpty(!markets.length, thunkFetchMarkets);

  const [state, setState] = useState<any>({
    reloadList: false,
    showAddTenantDialog: false,
    showEditTenantDialog: false,
    showAddDistributorManagerDialog: false,
    showReceiptEmailTemplateDialog: false,
    showDistributorDialog: false,
    addOrUpdateTenantResult: {}
  });

  const onTenantUpdated = () => {
    dispatch(thunkFetchTenants());
  };

  useEffect(() => {
    if (!tenants || !tenants.length || state.reloadList) {
      dispatch(thunkFetchTenants());
      dispatch(thunkFetchCurrentTenantLanguages());
    }
  }, [state]);

  useEffect(() => {
    if (tenants.length && markets.length && params.tenantId) {
      const editingTenant = tenants.find(sc => sc.id == params.tenantId);
      if (!editingTenant) {
        navigate(".", { replace: true });
        return;
      }

      setState({
        ...state,
        adding: false,
        editingTenant: {
          ...editingTenant,
          managers: getManagersForTenant(editingTenant)
        },
        showEditTenantDialog: true
      });
    }
  }, [tenants, markets]);

  const closeReceiptTemplateDialog = () => {
    setState({ ...state, showReceiptEmailTemplateDialog: false });
  };

  const getManagersForTenant = (tenant: Tenant): Person[] => {
    const managers = tenants
      .flatMap(x => {
        return x.managers.filter(y =>
          y.tenantAccessList.some(x => x === tenant.id)
        );
      })
      .filter(x => x != undefined);

    return [...new Set([...tenant.managers, ...managers])] as Person[];
  };

  return (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "",
            field: "logoUrl",
            render: (row: any) => {
              return (
                <Avatar
                  variant={"square"}
                  imgProps={{ style: { objectFit: "contain" } }}
                  src={row.logoUrl}
                  alt={row.name}
                >
                  {row.name[0]}
                </Avatar>
              );
            }
          },
          { title: t("common.formFields.name"), field: "name" },
          { title: t("common.formFields.company"), field: "companyName" },
          { title: t("common.formFields.orgNumber"), field: "orgNo" },
          {
            title: t("common.formFields.market"),
            field: "market",
            customSort: (a: any, b: any) => {
              return a.marketName.localeCompare(b.marketName);
            }
          },
          {
            title: t("common.formFields.tenantType"),
            field: "tenantType",
            render: (row: any) => (
              <Chip
                size={"small"}
                color={row.tenantType === "StoreKey" ? "primary" : "secondary"}
                variant={"outlined"}
                label={row.tenantType}
              />
            )
          },
          { title: t("common.formFields.managers"), field: "managers" },
          {
            title: t("common.tableHeaders.externalManagers"),
            field: "externalManagers"
          },
          {
            title: t("common.formFields.status"),
            field: "status",
            render: (row: any) => {
              switch (row.status) {
                case TenantStatus.OnBoardStep2Complete:
                  return (
                    <Tooltip
                      title={t("common.tooltip.readyForReviewAndConfig")}
                    >
                      <NotificationsActive
                        style={{ color: theme.palette.warning.dark }}
                      />
                    </Tooltip>
                  );
                case TenantStatus.OnBoardReviewAndConfigComplete:
                  return (
                    <Tooltip title={t("common.tooltip.verifiedAndConfigured")}>
                      <Verified style={{ color: theme.palette.success.dark }} />
                    </Tooltip>
                  );
                case TenantStatus.Active:
                  return (
                    <Tooltip title={t("common.tooltip.active")}>
                      <TaskAlt style={{ color: theme.palette.success.dark }} />
                    </Tooltip>
                  );
                default:
                  return (
                    <Tooltip
                      title={t("common.tooltip.waitingForCustomerOnboarding")}
                    >
                      <HourglassTop
                        style={{ color: theme.palette.grey.A100 }}
                      />
                    </Tooltip>
                  );
              }
            }
          }
        ]}
        isLoading={!tenants}
        data={
          tenants && tenants.length > 0
            ? tenants.map(sc => ({
                id: sc.id,
                name: sc.name,
                companyName: sc.companyName,
                orgNo: sc.orgNo,
                logoUrl: sc.logoUrl,
                tenantType: sc.tenantType.name,
                marketName: sc.market.name,
                market:
                  markets && markets.length ? (
                    <Tooltip
                      title={sc.market.name}
                      placement={"top-start"}
                      arrow
                    >
                      <Typography variant={"h5"}>
                        {getFlags(
                          markets.find(x => x.id === sc.market.id)
                            ?.countryCode as string
                        )}
                      </Typography>
                    </Tooltip>
                  ) : (
                    ""
                  ),
                managers: getManagersForTenant(sc)
                  .map(x => x.name)
                  .join(", "),
                externalManagers:
                  sc.externalPersonnel && sc.externalPersonnel.length
                    ? sc.externalPersonnel.map(x => x.name).join(", ")
                    : "",
                tenantLanguageIds: sc.languageIds,
                numberOfReceiptEmailTemplates: sc.numberOfReceiptEmailTemplates,
                status: sc.status
              }))
            : []
        }
        options={{
          pageSize: 20,
          actionsColumnIndex: -1
        }}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: t("common.messages.addNewTenant"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                editingTenant: { name: "", managers: [], externalManagers: [] },
                showEditTenantDialog: true,
                adding: true,
                reloadList: false
              });
            }
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editTenant"),
            onClick: (event, row: any) => {
              const editingTenant = tenants.find(sc => sc.id == row?.id);
              setState({
                ...state,
                adding: false,
                editingTenant: {
                  ...editingTenant,
                  managers: getManagersForTenant(editingTenant as Tenant)
                },
                showEditTenantDialog: true,
                reloadList: false
              });
              navigate(row?.id);
            }
          },
          rowData => ({
            icon: () => (
              <span
                className={`${
                  rowData.numberOfReceiptEmailTemplates == 0 ||
                  rowData.numberOfReceiptEmailTemplates <
                    rowData.tenantLanguageIds.length
                    ? classes.warning
                    : ""
                }`}
              >
                <Badge
                  badgeContent={
                    -(
                      rowData.tenantLanguageIds.length -
                      rowData.numberOfReceiptEmailTemplates
                    )
                  }
                  max={5}
                  color={"secondary"}
                >
                  <AttachEmail color="action" />
                </Badge>
              </span>
            ),
            tooltip: rowData.numberOfReceiptEmailTemplates
              ? t("common.messages.editReceiptEmailTemplate")
              : t("common.messages.configureAsap"),
            onClick: () => {
              const editingTenant = tenants.find(sc => sc.id == rowData?.id);
              setState({
                ...state,
                adding: false,
                editingTenant,
                showReceiptEmailTemplateDialog: true
              });
            }
          }),
          {
            icon: "post_add",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editContent"),
            onClick: (event, rowData: any) => {
              const editingTenant = tenants.find(t => t.id == rowData?.id);
              setState({
                ...state,
                showEditOnBoardingsDialog: true,
                editingTenant
              });
            }
          },
          {
            icon: "support_agent",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editSupport"),
            onClick: (event, rowData: any) => {
              const editingTenant = tenants.find(m => m.id == rowData?.id);
              setState({
                ...state,
                showEditSupportDialog: true,
                editingTenant: editingTenant
              });
            }
          },
          {
            icon: "car_rental",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.messages.addDistributionManager"),
            onClick: (event, row: any) => {
              const editingTenant = tenants.find(sc => sc.id == row?.id);
              setState({
                ...state,
                editingTenant: editingTenant,
                showAddDistributorManagerDialog: true
              });
            }
          },
          {
            icon: "local_shipping",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.manageDistributors"),
            onClick: (_event, row: any) => {
              const editingTenant = tenants.find(sc => sc.id == row?.id);
              setState({
                ...state,
                editingTenant: editingTenant,
                showDistributorDialog: true
              });
            }
          }
        ]}
        localization={{
          header: {
            actions: ""
          }
        }}
      />
      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showAddDistributorManagerDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">
            {t("common.messages.distributorManagers")}
          </Typography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() =>
              setState({ ...state, showAddDistributorManagerDialog: false })
            }
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ManageTenantDistributorManagers
            tenantId={state?.editingTenant?.id}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showDistributorDialog}
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">
            {t("common.messages.manageDistributorsTitle")}
          </Typography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => setState({ ...state, showDistributorDialog: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <ManageTenantDistributors tenantId={state?.editingTenant?.id} />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showEditTenantDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          {(state.editingTenant?.status === TenantStatus.OnBoardReady ||
            state.editingTenant?.status ===
              TenantStatus.OnBoardStep1Complete) && (
            <Tooltip
              open
              placement={"right-end"}
              title={t("common.tooltip.waitingForCustomerOnboarding")}
            >
              <HourglassTop style={{ color: theme.palette.grey.A100 }} />
            </Tooltip>
          )}
          {state.editingTenant?.status ===
            TenantStatus.OnBoardStep2Complete && (
            <Tooltip
              open
              placement={"right-end"}
              title={t("common.tooltip.readyForReviewAndConfig")}
            >
              <NotificationsActive
                style={{ color: theme.palette.warning.dark }}
              />
            </Tooltip>
          )}

          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showEditTenantDialog: false });
              navigate(".", { replace: true });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <TenantEditor
            tenant={state.editingTenant}
            user={user}
            adding={state.adding}
            onSubmit={() => {
              setState({
                ...state,
                showEditTenantDialog: false,
                reloadList: true
              });
              navigate(".", { replace: true });
            }}
          />
        </DialogContent>
      </Dialog>
      <ReceiptEmailTemplateEditor
        onEditSuccess={onTenantUpdated}
        tenant={state.editingTenant}
        dialogOpen={state.showReceiptEmailTemplateDialog}
        closeDialog={closeReceiptTemplateDialog}
      />

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showEditSupportDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => setState({ ...state, showEditSupportDialog: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <SupportEditor
            tenantId={state.editingTenant?.id}
            support={state.editingTenant?.content?.support}
            onSave={() => {
              setState({
                ...state,
                reloadList: true,
                upsertSuccess: true,
                showEditSupportDialog: false
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={state.showEditOnBoardingsDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() =>
              setState({ ...state, showEditOnBoardingsDialog: false })
            }
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <OnBoardingEditor
            tenantId={state.editingTenant?.id}
            tenants={tenants}
            onSave={() => {
              setState({
                ...state,
                reloadList: true
              });
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar
        open={state.upsertSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            upsertSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {t("common.messages.successfullyAddedUpdatedTenant")}
        </Alert>
      </Snackbar>
    </>
  );
};

type EditorPropType = {
  tenant: Tenant;
  user: any;
  onSubmit: () => void;
  adding: boolean;
};

function TenantEditor({ tenant, user, onSubmit, adding }: EditorPropType) {
  const [editingTenant, setTenant] =
    useState<Omit<Tenant, "configuration">>(tenant);
  const classes = useStyles();
  const [editOrUpdateTenantFailed, setEditOrUpdateTenantFailed] =
    useState(false);

  const { t } = useI18n();

  const [upsertLogo, setUpsertLogo] = useState<ImageInformation>();
  const [upsertBanner, setUpsertBanner] = useState<ImageInformation>();

  const [config, dispatch] = useReducer<Reducer<TenantConfType, ConfigAction>>(
    (config, action) => configReducer(config, action, t),
    {
      ...tenant.configuration,
      features: {
        digitalPriceTags: false,
        centralOrderingSystem: false,
        usesOpenPricing: false,
        requiresPaymentMethod: false,
        availableInStoreKey: false,
        deliveryThroughFileUpload: false,
        usesNotVisitedStoresLockOut: false,
        sendReceiptEmail: false,
        hasMultipleCombos: false,
        clickNCollect: false,
        delivery: false,
        ...tenant.configuration?.features
      }
    }
  );

  const upload = async (id: string, image: ImageInformation, type: string) => {
    if (!image?.blob) return;

    const result = await TenantApi.uploadImage(id, image.blob, type);

    if (result.type !== "OK") setEditOrUpdateTenantFailed(true);
  };

  const disabled = () => {
    return (
      !editingTenant?.name ||
      !editingTenant?.managers ||
      editingTenant?.managers.length <= 0 ||
      !editingTenant?.languageIds ||
      editingTenant?.languageIds.length <= 0 ||
      !editingTenant?.market
    );
  };

  const submit = async () => {
    if (adding) {
      const result = await TenantApi.add({
        ...editingTenant,
        status: TenantStatus.OnBoardReady
      });
      if (result.type !== "OK") {
        setEditOrUpdateTenantFailed(true);
        return;
      }

      if (upsertLogo) await upload(result.value, upsertLogo, "logo");
      if (upsertBanner) await upload(result.value, upsertBanner, "banner");
    } else {
      const result = await TenantApi.update({
        ...editingTenant,
        configuration: config,
        status:
          editingTenant.status === TenantStatus.OnBoardReviewAndConfigComplete
            ? TenantStatus.OnBoardReviewAndConfigComplete
            : editingTenant.status
      });
      if (result.type !== "OK") {
        setEditOrUpdateTenantFailed(true);
        return;
      }

      if (upsertLogo) await upload(editingTenant.id, upsertLogo, "logo");
      if (upsertBanner) await upload(editingTenant.id, upsertBanner, "banner");
    }
    onSubmit();
  };

  return (
    <>
      <TenantTypeSelect
        className={classes.field}
        value={editingTenant?.tenantType?.id}
        onSelected={selectedTenantType =>
          setTenant({
            ...editingTenant,
            tenantType: selectedTenantType
          })
        }
      />
      <TextField
        className={classes.field}
        label={t("common.formFields.name")}
        value={editingTenant?.name}
        onChange={e =>
          setTenant({
            ...editingTenant,
            name: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.companyName")}
        value={editingTenant?.companyName}
        onChange={e =>
          setTenant({
            ...editingTenant,
            companyName: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.orgNumber")}
        value={editingTenant?.orgNo}
        onChange={e =>
          setTenant({
            ...editingTenant,
            orgNo: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.contactName")}
        value={editingTenant?.contactName}
        onChange={e =>
          setTenant({
            ...editingTenant,
            contactName: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.contactEmail")}
        value={editingTenant?.contactEmail}
        onChange={e =>
          setTenant({
            ...editingTenant,
            contactEmail: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.iban")}
        value={editingTenant?.iban}
        onChange={e =>
          setTenant({
            ...editingTenant,
            iban: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.bicSwift")}
        value={editingTenant?.bic}
        onChange={e =>
          setTenant({
            ...editingTenant,
            bic: e.target.value
          })
        }
      />
      <TextField
        className={classes.field}
        label={t("common.formFields.address")}
        value={editingTenant?.address}
        onChange={e =>
          setTenant({
            ...editingTenant,
            address: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.postalCode")}
        value={editingTenant?.postalCode}
        onChange={e =>
          setTenant({
            ...editingTenant,
            postalCode: e.target.value
          })
        }
      />
      <TextField
        className={classes.fieldInline}
        label={t("common.formFields.city")}
        value={editingTenant?.city}
        onChange={e =>
          setTenant({
            ...editingTenant,
            city: e.target.value
          })
        }
      />
      <MarketSelect
        label={t("common.formFields.market") + " *"}
        required
        className={classes.field}
        value={editingTenant?.market?.id.toString()}
        onSelected={market => setTenant({ ...editingTenant, market: market })}
      />
      <LanguageMultiSelect
        label={t("common.formFields.language") + " *"}
        required
        className={classes.field}
        value={editingTenant?.languageIds}
        onSelected={languages =>
          setTenant({
            ...editingTenant,
            languageIds: languages.map((x: any) => x.id)
          })
        }
      />
      <PersonMultiSelect
        includeExternal={false}
        label={t("common.formFields.managers") + " *"}
        className={classes.field}
        value={editingTenant?.managers?.map((x: any) => x.id)}
        onSelected={managers =>
          setTenant({ ...editingTenant, managers: managers })
        }
      />
      <PersonMultiSelect
        includeExternal={true}
        label={t("common.formFields.externalPersonnel")}
        className={classes.field}
        value={
          editingTenant?.externalPersonnel &&
          editingTenant.externalPersonnel.length
            ? editingTenant.externalPersonnel.map((x: any) => x.id)
            : []
        }
        hide={
          editingTenant?.externalPersonnel &&
          editingTenant.externalPersonnel.length
            ? editingTenant.externalPersonnel.filter(
                (x: any) => x.isDistributorManager
              )
            : []
        }
        onSelected={externalPersonnel =>
          setTenant({
            ...editingTenant,
            externalPersonnel: externalPersonnel
          })
        }
      />
      <LoginMethodOrderedSelects
        label={t("common.formFields.loginMethods")}
        className={classes.field}
        value={editingTenant.loginMethods ?? []}
        onSelected={loginMethods =>
          setTenant({
            ...editingTenant,
            loginMethods: loginMethods
          })
        }
      />
      {/*TODO Make generic in future*/}
      {editingTenant.loginMethods?.some(x => x.name === "smsOtp") && (
        <Alert style={{ marginBottom: 20 }} severity={"info"}>
          <AlertTitle>{t("common.messages.verificationMethodInfo")}</AlertTitle>
          <VerificationMethodMultiSelect
            label={t("common.formFields.verificationMethods")}
            className={classes.field}
            value={editingTenant?.verificationMethods ?? []}
            onSelected={verificationMethods =>
              setTenant({
                ...editingTenant,
                verificationMethods: verificationMethods
              })
            }
          />
        </Alert>
      )}
      <PaymentProviderMultiSelect
        label={t("common.formFields.paymentProviders")}
        className={classes.field}
        value={editingTenant?.paymentProviders ?? []}
        onSelected={paymentProviders =>
          setTenant({
            ...editingTenant,
            paymentProviders: paymentProviders
          })
        }
      />
      <ImageEditor
        title={t("common.formFields.logo") + " (300x300px)"}
        acceptedFiles={["image/png"]}
        initial={{
          ...upsertLogo,
          url: editingTenant?.logoUrl ?? ""
        }}
        onChange={setUpsertLogo}
      />
      <ImageEditor
        title={t("common.formFields.banner") + " (800x200px)"}
        acceptedFiles={["image/png"]}
        initial={{
          ...upsertBanner,
          url: editingTenant?.bannerUrl ?? ""
        }}
        onChange={setUpsertBanner}
      />

      <div>
        {hasRole(user, ["Root"]) && (
          <div style={{ marginTop: 20 }}>
            <FeatureCard
              features={config.features!}
              onChange={dispatch}
              t={t}
            />
            <JSONInput
              id="jsonConfigEditor"
              placeholder={config}
              locale={locale}
              height="550px"
              wd
              onChange={(jsonChange: any) =>
                dispatch({ type: "Config", value: jsonChange.json })
              }
            />
          </div>
        )}
      </div>
      {!adding && (
        <Alert
          style={{ marginTop: 20 }}
          severity={
            editingTenant?.status ===
              TenantStatus.OnBoardReviewAndConfigComplete ||
            editingTenant?.status === TenantStatus.Active
              ? "success"
              : "info"
          }
        >
          <AlertTitle>{t("common.messages.verifySteps")}</AlertTitle>
          <List disablePadding>
            <ListItem>1. {t("common.messages.tenantInfoComplete")}</ListItem>
            <ListItem>2. {t("common.messages.tenantConfigured")}</ListItem>
            <ListItem>3. {t("common.messages.storeInfoComplete")}</ListItem>
            <ListItem>4. {t("common.messages.storeConfigured")}</ListItem>
          </List>
          <FormControlLabel
            control={
              <Checkbox
                disabled={
                  editingTenant?.status ===
                    TenantStatus.OnBoardReviewAndConfigComplete ||
                  editingTenant?.status === TenantStatus.Active
                }
                checked={
                  editingTenant?.status ===
                    TenantStatus.OnBoardReviewAndConfigComplete ||
                  editingTenant?.status === TenantStatus.Active
                }
                onChange={e => {
                  return setTenant({
                    ...editingTenant,
                    status: e.target.checked
                      ? TenantStatus.OnBoardReviewAndConfigComplete
                      : ""
                  });
                }}
              />
            }
            label={t("common.formFields.confirmTenantReady")}
          />
        </Alert>
      )}
      <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
        <Button
          style={{ width: 200 }}
          variant="contained"
          color="primary"
          disabled={disabled()}
          onClick={submit}
        >
          {t("common.formFields.saveTenant")}
        </Button>
        {editOrUpdateTenantFailed && (
          <Alert severity="error">{t("common.messages.tenantError")}</Alert>
        )}
      </div>
    </>
  );
}

type ConfigAction =
  | { type: FeatureType; value: boolean }
  | { type: "Config"; value: string };

function configReducer(
  config: TenantConfType,
  action: ConfigAction,
  t: (key: string) => string
) {
  if (action.type === "Config") {
    try {
      return JSON.parse(action.value);
    } catch (e) {
      console.error(t("common.messages.jsonConfigSyntaxError"));
      return config;
    }
  }
  return produce(config, draftState => {
    switch (action.type) {
      case "digitalPriceTags":
        draftState.features!.digitalPriceTags = action.value;
        break;
      case "centralOrderingSystem":
        draftState.features!.centralOrderingSystem = action.value;
        break;
      case "clickNCollect":
        draftState.features!.clickNCollect = action.value;
        break;
      case "usesOpenPricing":
        draftState.features!.usesOpenPricing = action.value;
        break;
      case "requiresPaymentMethod":
        draftState.features!.requiresPaymentMethod = action.value;
        break;
      case "availableInStoreKey":
        draftState.features!.availableInStoreKey = action.value;
        break;
      case "deliveryThroughFileUpload":
        draftState.features!.deliveryThroughFileUpload = action.value;
        break;
      case "usesNotVisitedStoresLockOut":
        draftState.features!.usesNotVisitedStoresLockOut = action.value;
        break;
      case "sendReceiptEmail":
        draftState.features!.sendReceiptEmail = action.value;
        break;
      case "hasMultipleCombos":
        draftState.features!.hasMultipleCombos = action.value;
        break;
      case "delivery":
        draftState.features!.delivery = action.value;
        break;

      default:
        console.error(t("common.messages.unknownAction"), action);
        return config;
    }
  });
}

type FeatureCardProps = {
  features: Features;
  onChange: (e: ConfigAction) => void;
};
const FeatureCard = ({
  features,
  onChange,
  t
}: FeatureCardProps & { t: (key: string) => string }) => {
  const PrimarySettings = withPrimaryIcon(SettingsIcon);
  return (
    <Card>
      <CardHeader
        title={t("common.actions.enableFeatures")}
        avatar={<PrimarySettings />}
        titleTypographyProps={{ variant: "h6" }}
      />
      <CardContent>
        <List>
          {Object.entries(features).map(([feature, setting], i) => (
            <>
              {i > 0 && <Divider variant={"middle"} />}
              <ListItem>
                <ListItemText primary={feature} />
                <ListItemSecondaryAction>
                  <Checkbox
                    checked={setting}
                    onChange={e =>
                      onChange({
                        type: feature as FeatureType,
                        value: e.target.checked
                      })
                    }
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
